import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { resolveLandingPath } from "@/lib/landing";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { Loader2, ShieldCheck, PlayCircle } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, demoLogin, register, googleLogin } = useAuth();
  const config = usePublicConfig();
  const ssoCfg = config?.sso;
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      toast.success("Signed in");
      // Role-aware landing (shared with SSO): board-only members go straight to Forum View.
      navigate(await resolveLandingPath());
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  // Demo personas are passwordless: the backend resolves the account by key in demo mode.
  const loginAs = async (p) => {
    setErr(""); setBusy(true);
    try {
      await demoLogin(p.key);
      toast.success(`Exploring GovFlowState as ${p.label}`);
      navigate(await resolveLandingPath());
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const inputCls = "mt-1 w-full h-11 rounded-lg bg-brand-bg border border-brand-line px-3 text-brand-ink placeholder:text-slate-400 outline-none focus:bg-white focus:border-brand-civic transition-colors";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-brand-bg">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden"
        style={{ backgroundImage: "linear-gradient(135deg, rgba(11,40,66,0.94), rgba(14,50,82,0.86)), url(https://images.unsplash.com/photo-1745610976614-bf51adc509a7?crop=entropy&cs=srgb&fm=jpg&q=85)", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center shadow-sm ring-1 ring-white/40 overflow-hidden"><img src="/govflow-mark.png" alt="GovFlowState" className="h-8 w-8 object-contain" /></div>
          <div><div className="font-display font-bold text-white text-lg tracking-tight">GovFlowState</div><div className="text-xs uppercase tracking-widest text-brand-civic/90">Legislative Workflow &amp; Intelligence</div></div>
        </div>
        <div>
          <h2 className="font-display text-4xl font-extrabold text-white leading-tight">Legislative intelligence<br />for state government.</h2>
          <p className="text-slate-200 mt-4 max-w-md leading-relaxed">Monitor bills, assign staff, track activity in near real time, and generate reports — a purpose-built platform for statehouse operations.</p>
          <div className="flex items-center gap-2 mt-6 text-slate-300 text-sm"><ShieldCheck className="h-4 w-4 text-brand-gold" /> Role-based access · Audit logged</div>
        </div>
        <div className="text-xs text-slate-400">GovFlowState is a product of Rich AI Labs</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center ring-1 ring-brand-line overflow-hidden"><img src="/govflow-mark.png" alt="GovFlowState" className="h-7 w-7 object-contain" /></div>
            <div className="font-display font-bold text-brand-ink text-lg">GovFlowState</div>
          </div>
          <h1 className="font-display text-2xl font-extrabold text-brand-ink">{mode === "login" ? "Sign in" : "Create account"}</h1>
          <p className="text-slate-500 text-sm mt-1 mb-6">{mode === "login" ? "Access your legislative workspace." : "New accounts start with Viewer access."}</p>

          <button onClick={googleLogin} data-testid="google-login-button"
            className="w-full h-11 rounded-lg bg-white border border-brand-line text-brand-ink font-semibold flex items-center justify-center gap-2 hover:bg-slate-50 hover:border-brand-civic/40 transition-colors">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" /> Continue with Google
          </button>
          {ssoCfg?.enabled && ssoCfg?.configured && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}${ssoCfg.login_path}`} data-testid="sso-login-button"
              className="mt-3 w-full h-11 rounded-lg bg-brand-navy text-white font-semibold flex items-center justify-center gap-2 hover:bg-brand-navy-700 transition-colors">
              <ShieldCheck className="h-5 w-5" /> Sign in with {ssoCfg.provider || "Enterprise SSO"}
            </a>
          )}
          <div className="flex items-center gap-3 my-5"><div className="h-px flex-1 bg-brand-line" /><span className="text-xs text-slate-400 uppercase tracking-wider">or</span><div className="h-px flex-1 bg-brand-line" /></div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div>
                <label className="text-xs font-medium text-slate-500" htmlFor="name">Full name</label>
                <input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="name-input" className={inputCls} />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-500" htmlFor="email">Email</label>
              <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="email-input" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500" htmlFor="password">Password</label>
              <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="password-input" className={inputCls} />
            </div>
            {err && <p className="text-sm text-red-500" data-testid="login-error">{err}</p>}
            <button type="submit" disabled={busy} data-testid="submit-auth-button"
              className="w-full h-11 rounded-lg bg-brand-navy text-white font-bold flex items-center justify-center gap-2 hover:bg-brand-navy-700 transition-colors disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="mt-4 text-sm font-medium text-brand-civic hover:text-brand-navy" data-testid="toggle-auth-mode">
            {mode === "login" ? "Need an account? Register" : "Have an account? Sign in"}
          </button>

          {mode === "login" && config?.demo?.enabled && (
            <div className="mt-6 rounded-lg border border-brand-civic/30 bg-brand-civic-50 p-4" data-testid="demo-access-panel">
              <div className="flex items-center gap-2">
                <PlayCircle className="h-4 w-4 text-brand-civic" />
                <p className="font-display text-sm font-bold text-brand-navy">Explore GovFlowState as any role</p>
              </div>
              <p className="text-xs text-slate-500 mt-0.5 mb-3">Step into the SAMPLE environment from any perspective. Clearly-labeled demonstration data — not live records.</p>
              <a href="/demo/legislative-lifecycle" data-testid="guided-demo-link" className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-civic hover:underline mb-3">▶ Start the guided flagship demo (10–15 min)</a>
              <div className="grid grid-cols-2 gap-2">
                {(config.demo.personas || [{ key: "resident", label: "Demo", role_label: "Read-only", description: "Explore the demo" }]).map((p) => (
                  <button key={p.key} type="button" onClick={() => loginAs(p)} disabled={busy} title={p.description}
                    data-testid={`demo-persona-${p.key}`}
                    className="text-left rounded-lg border border-brand-line bg-white px-3 py-2 hover:border-brand-civic/50 hover:shadow-card transition-all disabled:opacity-60">
                    <div className="text-xs font-bold text-brand-navy leading-tight">{p.label}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{p.role_label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
